import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import '../../CSS/Global.css';
import './CustomerStyle.css';
import Header from '../Header';
import Sidebar from '../SideBar';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import {
  BadgeCheck,
  BadgeIndianRupee,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Receipt,
  Search,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { decryptToken } from '../utils/getToken';
import { jsonToXlsxBlob, triggerDownload } from '../../utils/excelSheet';
import { admissionStatusLabel, admissionStatusRowClass, formatAdmissionDateTimeIST } from './admissionFormat';
import { admissionAssetUrl, isPdfPath } from './admissionFormUpload';
import EmptyStateBlock from '../EmptyStateBlock';
import AdmissionPageSummary from './AdmissionPageSummary';

/** Status values available in header filter (matches admission API / row dropdown). */
const HEADER_STATUS_FILTERS = ['Active', 'Deactive', 'Paid', 'Failed'];

function AdmissionCardAvatar({ apiBase, studentImage }) {
  const [broken, setBroken] = useState(false);
  const hasImg = Boolean(studentImage && !isPdfPath(studentImage));
  const src = hasImg ? admissionAssetUrl(apiBase, studentImage) : "";

  useEffect(() => {
    setBroken(false);
  }, [studentImage]);

  if (!hasImg || broken) {
    return (
      <div className="admission-card__avatar admission-card__avatar--placeholder" aria-hidden>
        <User size={26} strokeWidth={2} />
      </div>
    );
  }

  return (
    <div className="admission-card__avatar">
      <img key={src} src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
    </div>
  );
}

const ADMISSION_SAMPLE_ROW = {
  "Admission ID": "",
  "Payment ID": "",
  "Name": "",
  "Father Name": "",
  "Mother Name": "",
  "Date of Birth": "",
  "Aadhar": "",
  "Gender": "",
  "School": "",
  "Address": "",
  "Pincode": "",
  "Mobile": "",
  "Email": "",
  "Center": "",
  "Batch ID": "",
  "Batch Name": "",
  "Status": "",
  "Rules Agreed": "",
  "Created At": "",
};

const Admission = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  /** 'all' | admission API status — quick filters from header dropdown */
  const [statusFilter, setStatusFilter] = useState('all');
  const [summaryPeriod, setSummaryPeriod] = useState('All Time');
  const [isImportOpen, setImportOpen] = useState(false);
  const admissionFilterRef = useRef(null);
  const [admissionSearch, setAdmissionSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const url = API_BASE_URL
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
  const [load, setLoad] = useState(false)
   const token = decryptToken("accessToken") || ""

  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const canBulkDelete = useCallback(() => {
    if (!token) return false;
    try {
      const d = jwtDecode(token);
      const r = String(d.role ?? d.type ?? "").toLowerCase();
      return r === "admin" || r === "superadmin";
    } catch {
      return false;
    }
  }, [token]);

  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (!bulkSelectMode) setSelectedIds([]);
  }, [bulkSelectMode]);

  const toggleSelectId = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const fetchCustomers = async () => {
    if (!isLoggedIn?.main_id) {
      setCustomers([]);
      return;
    }
    try {
      setLoad(true); // Start loading
      const response = await fetch(`${url}/admission/alladmin/${isLoggedIn.main_id}`, {
        headers: authHeaders,
      });
      const data = await response.json();
      // const parsedCustomers = data.map((customer) => ({
      //   ...customer,
      //   billingAddress: customer.billingAddress ? JSON.parse(customer.billingAddress) : {},
      //   shippingAddress: customer.shippingAddress ? JSON.parse(customer.shippingAddress) : {},
      //   contact: customer.contact ? JSON.parse(customer.contact) : [],
      // }));
      console.log("Customer data", data);
      const rows = Array.isArray(data?.user) ? data.user : [];
      setCustomers(rows);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setLoad(false); // Stop loading after everything is done
    }
  };


  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const onDown = (e) => {
      if (admissionFilterRef.current && !admissionFilterRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isDropdownOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [admissionSearch, globalSearch, statusFilter, summaryPeriod, customDate.from, customDate.to, sortOrder]);

  // 1. Filter by date (API may return non-array on error — always treat as array)
  const list = Array.isArray(customers) ? customers : [];
  let processedCustomers = [...list];
  if (customDate.from || customDate.to) {
    processedCustomers = processedCustomers.filter((customer) => {
      const createdAt = new Date(customer.createdAt);
      const from = customDate.from ? new Date(customDate.from) : null;
      const to = customDate.to ? new Date(customDate.to) : null;

      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  if (HEADER_STATUS_FILTERS.includes(statusFilter)) {
    processedCustomers = processedCustomers.filter((c) => c.status === statusFilter);
  }

  // 2. Sort by createdAt
  processedCustomers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });


  const searchRaw = (admissionSearch.trim() || globalSearch.trim()).toLowerCase();
  const q = searchRaw;
  const filteredCustomers = processedCustomers.filter((row) => {
    if (!q) return true;
    const hay = [
      row.name,
      row.fatherName,
      row.motherName,
      row.email,
      row.mobile,
      row.admission_id,
      row.payment_id,
      row.center,
      row.batch?.name,
      row.school,
      row.status,
      row.aadhar,
      row.student_id,
      row.address,
      row.pincode,
      row.gender,
      row.batch_id != null ? String(row.batch_id) : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });

  const indexOfLastCustomer = currentPage * entriesPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / entriesPerPage) || 1);

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const totalLoaded = list.length;
  const totalInView = processedCustomers.length;
  const totalMatching = filteredCustomers.length;
  const searchActive = Boolean(admissionSearch.trim() || globalSearch.trim());
  const pageLabelFrom = totalMatching === 0 ? 0 : indexOfFirstCustomer + 1;
  const pageLabelTo = Math.min(indexOfLastCustomer, totalMatching);

  const selectAllOnCurrentPage = () => {
    const pageIds = currentCustomers
      .map((c) => c.id)
      .filter((x) => x != null)
      .map(Number)
      .filter(Number.isFinite);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete() || selectedIds.length === 0) return;

    const rows = customers.filter((c) => selectedIds.includes(Number(c.id)));
    const paidCount = rows.filter((c) => String(c.status) === "Paid").length;

    const first = await Swal.fire({
      title: `Delete ${selectedIds.length} admission(s)?`,
      html: `<p style="text-align:left;margin:0 0 8px">This permanently removes selected records, documents, and links. Invoices or payments elsewhere are not automatically reversed.</p><p style="text-align:left;margin:0;font-size:13px;color:#64748b">You cannot undo this action.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Continue",
      focusCancel: true,
    });
    if (!first.isConfirmed) return;

    if (paidCount > 0) {
      const second = await Swal.fire({
        title: `${paidCount} Paid admission(s) selected`,
        input: "text",
        inputLabel: "Type DELETE in capitals to confirm",
        inputPlaceholder: "DELETE",
        showCancelButton: true,
        confirmButtonColor: "#b91c1c",
        confirmButtonText: "Delete forever",
        inputValidator: (v) => (v === "DELETE" ? null : "Type DELETE exactly to proceed"),
      });
      if (!second.isConfirmed) return;
    }

    setBulkDeleting(true);
    try {
      const res = await fetch(`${url}/admission/bulk-delete`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || "Bulk delete failed");
      }
      const deleted = typeof data.deleted === "number" ? data.deleted : selectedIds.length;
      const removedSet = new Set(selectedIds);
      setCustomers((prev) =>
        Array.isArray(prev) ? prev.filter((c) => !removedSet.has(Number(c.id))) : []
      );
      clearSelection();
      setBulkSelectMode(false);
      let successText = data.message || `Removed ${deleted} admission(s).`;
      if (data.notDeleted > 0) {
        successText += ` ${data.notDeleted} id(s) were skipped (not found or not permitted).`;
      }
      await Swal.fire({
        icon: data.notDeleted > 0 ? "warning" : "success",
        title: "Bulk delete complete",
        text: successText,
        confirmButtonColor: "#2563eb",
      });
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e.message || "Bulk delete failed", "error");
    } finally {
      setBulkDeleting(false);
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);

  const selectStatusFilter = (next) => {
    setStatusFilter(next);
    setDropdownOpen(false);
    setCurrentPage(1);
  };

  const admissionHeaderTitle =
    statusFilter === 'all' ? 'All admissions' : admissionStatusLabel(statusFilter);
  const toggleImport = () => setImportOpen(!isImportOpen);

  const handleDownloadAdmissionSample = async () => {
    const blob = await jsonToXlsxBlob([ADMISSION_SAMPLE_ROW], "Admissions");
    triggerDownload(blob, "admission_import_sample.xlsx");
    setImportOpen(false);
  };

  const handleDelete = async (customerId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This customer will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${url}/admission/${customerId}`, {
            method: 'DELETE',
            headers:{
              "Content-Type":"application/json",
              "Authorization":`Bearer ${token}`
            }
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err?.message || 'Failed to delete');
          }

          Swal.fire('Deleted!', 'Admission has been deleted.', 'success');
          setCustomers((prev) => (Array.isArray(prev) ? prev.filter((c) => c.id !== customerId) : []));
        } catch (error) {
          console.error('Delete error:', error.message);
          Swal.fire('Error!', `Failed to delete customer. ${error.message}`, 'error');
        }
      }
    });
  };


  const handleEdit = (customer) => {
    navigate('/edit-admission', { state: customer });

  };

  const handleStatusChange = async (e, customer) => {
    e.stopPropagation();
    const newStatus = e.target.value;

    // Optimistic UI update
    setCustomers(prev =>
      prev.map(c =>
        c.id === customer.id ? { ...c, status: newStatus } : c
      )
    );

    try {
      const res = await fetch(`${url}/admission/status/${customer.id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        throw new Error("Failed to update status");
      }

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Status updated successfully",
        timer: 1200,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error(error);

      // Rollback on error
      setCustomers(prev =>
        prev.map(c =>
          c.id === customer.id ? { ...c, status: customer.status } : c
        )
      );

      Swal.fire("Error", "Failed to update status", "error");
    }
  };


  // const handleDelete=()=>{

  // }


  const SkeletonLoader = () => (
    <div>
      {Array(10).fill('').map((_, index) => (
        <div className="skeleton-row" key={index}>
          {[...Array(5)].map((_, i) => <div className="skeleton" key={i}></div>)}
        </div>
      ))}
    </div>
  );

  const renderStatusRow = (customer) => {
    if (!customer.status) return null;
    const sc = admissionStatusRowClass(customer.status);
    const rowCls = sc ? `ecom-customer-row${sc}` : "ecom-customer-row";
    const iconCls = sc ? `ecom-customer-icon${sc}` : "ecom-customer-icon";
    return (
      <div className={`${rowCls} admission-card__row admission-card__row--status`}>
        <BadgeIndianRupee className={iconCls} strokeWidth={2} />
        <span>{admissionStatusLabel(customer.status)}</span>
      </div>
    );
  };

  if (load) {
    return (
      <Loader />
    );
  }

  return (
    <>
      <AdmissionPageSummary
        admissions={list}
        period={summaryPeriod}
        onPeriodChange={setSummaryPeriod}
        statusFilter={statusFilter}
        onStatusFilterChange={selectStatusFilter}
      />

      {statusFilter !== 'all' && (
        <div className="admission-summary-filter-banner" role="status">
          <span>
            Showing <strong>{admissionStatusLabel(statusFilter)}</strong> admissions
            {totalMatching > 0 ? ` (${totalMatching} in view)` : ''}
          </span>
          <button
            type="button"
            className="admission-summary-filter-banner__clear"
            onClick={() => selectStatusFilter('all')}
          >
            Clear filter
          </button>
        </div>
      )}

      <div
        className={`admission-page${
          bulkSelectMode && canBulkDelete() && selectedIds.length > 0 ? " admission-page--bulk-dock" : ""
        }`}
      >
        <div className="admission-controls">
        <div className="customers-header admission-page__toolbar admission-page__toolbar--integrated">
          <div className="customer-dropdown admission-status-filter-anchor" ref={admissionFilterRef}>
            <p className="dashboard-title" onClick={toggleDropdown}>
              {admissionHeaderTitle}{' '}
              <i className={`fa-solid fa-chevron-down ${isDropdownOpen ? 'rotate' : ''}`}></i>
            </p>
            <div className="custom-header-ul">
              {isDropdownOpen && (
                <div className="customer-dropdown-list admission-status-filter-dropdown">
                  <div className="customer-dropdown-list-default">
                    <p className="default-filter">
                      <i className="fa-solid fa-chevron-right"></i>
                      Filter by status
                    </p>
                    <ul className="customer-list-ul admission-status-filter-list">
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'all' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('all')}
                        >
                          All admissions
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Active' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Active')}
                        >
                          Active
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Deactive' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Deactive')}
                        >
                          Deactive
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Paid' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Paid')}
                        >
                          Paid
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`admission-status-filter-btn${statusFilter === 'Failed' ? ' admission-status-filter-btn--active' : ''}`}
                          onClick={() => selectStatusFilter('Failed')}
                        >
                          Failed
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="customer-warning-new-hamburger-help">
            {/* <p className="customer-warning-text">
              <span><i className="fa-solid fa-triangle-exclamation"></i> Update GST details</span> for 520 customers
            </p> */}
            <div className="customer-new admission-toolbar-actions">
              {canBulkDelete() && (
                <button
                  type="button"
                  className={`admission-bulk-mode-btn${bulkSelectMode ? " admission-bulk-mode-btn--active" : ""}`}
                  onClick={() => setBulkSelectMode((m) => !m)}
                  aria-pressed={bulkSelectMode}
                >
                  {bulkSelectMode ? "Cancel select" : "Select"}
                </button>
              )}
              <Link to='/add-admission'>
                <button className="oms-add-btn"><i className="fa-solid fa-plus"></i> New</button>
              </Link>
            </div>
            <div className="filter-controls">
              <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
                <i className="fa-solid fa-sliders"></i> Filters
              </button>
            </div>
            <div className="import-hamburger" onClick={toggleImport}>
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </div>
            {isImportOpen && (
              <div className="import-ul">
                <ul>
                  <li>
                    <Link to="/admission-centers-settings" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-location-dot"></i> Manage centers
                    </Link>
                  </li>
                  <li>
                    <Link to="/admission-batches" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-table-cells"></i> Batches
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Link to="/admission-import" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-file-arrow-down"></i> Import
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Link to="/export-admission" onClick={() => setImportOpen(false)}>
                      <i className="fa-solid fa-file-arrow-up"></i> Export list
                    </Link>
                  </li>
                </ul>
                <ul>
                  <li onClick={handleDownloadAdmissionSample}>
                    <i className="fa-solid fa-file-arrow-down"></i> Download Sample Excel
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {bulkSelectMode && canBulkDelete() && (
          <div className="admission-bulk-strip admission-bulk-strip--embedded" role="region" aria-label="Bulk selection">
            <div className="admission-bulk-strip__inner">
              <span className="admission-bulk-strip__count">
                <strong>{selectedIds.length}</strong> selected
                {filteredCustomers.length > 0 && (
                  <span className="admission-bulk-strip__hint"> · across pages when you navigate</span>
                )}
              </span>
              <div className="admission-bulk-strip__btns">
                <button type="button" className="admission-bulk-strip__linkish" onClick={selectAllOnCurrentPage}>
                  All on this page
                </button>
                <button type="button" className="admission-bulk-strip__linkish" onClick={clearSelection} disabled={selectedIds.length === 0}>
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="admission-controls__search">
          <div className="admission-search-summary__search">
            <Search className="admission-search-summary__icon" size={17} strokeWidth={2.25} aria-hidden />
            <input
              type="search"
              className="admission-search-summary__input"
              placeholder="Search name, ID, phone, email, center…"
              value={admissionSearch}
              onChange={(e) => setAdmissionSearch(e.target.value)}
              aria-label="Search admissions"
            />
            {admissionSearch.trim() !== "" && (
              <button
                type="button"
                className="admission-search-summary__clear admission-search-summary__clear--icon"
                onClick={() => setAdmissionSearch("")}
                aria-label="Clear search"
              >
                <X size={18} strokeWidth={2.25} aria-hidden />
              </button>
            )}
          </div>
          <div className="admission-search-summary__stats" role="status">
            <span className="admission-search-summary__pill" title="After date range and status filter">
              <strong>{totalInView}</strong> admission{totalInView === 1 ? "" : "s"}
            </span>
            {totalInView !== totalLoaded && (
              <span className="admission-search-summary__pill admission-search-summary__pill--muted">
                <strong>{totalLoaded}</strong> loaded overall
              </span>
            )}
            {searchActive && (
              <span className="admission-search-summary__pill admission-search-summary__pill--accent">
                <strong>{totalMatching}</strong> match{totalMatching === 1 ? "" : "es"} search
              </span>
            )}
            {totalMatching > 0 && (
              <span className="admission-search-summary__range">
                Rows {pageLabelFrom}–{pageLabelTo} of {totalMatching}
              </span>
            )}
          </div>
        </div>
        </div>

        {/* <div className="customer-table">
          {loading ? (
            <SkeletonLoader />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Work Phone</th>
                  <th>GST Number</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {
                  currentCustomers.length > 0 ? (

                    currentCustomers.map((customer, index) => (
                      <tr key={index}>
                        <td>{customer.customerName}</td>
                        <td>{customer.companyName}</td>
                        <td>{customer.email}</td>
                        <td>{customer.phone1}</td>
                        <td>{customer.gstNumber}</td>
                        <td>{new Date(customer.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                        <td>
                          <button onClick={() => handleEdit(customer)} className="edit-btn">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button onClick={() => handleDelete(customer.id)} className="delete-btn" style={{ marginLeft: '8px' }}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center' }}>No data found.</td>
                    </tr>
                  )}
              </tbody>
            </table>
          )}
        </div> */}
        <div className="admission-cards-grid">
        {
          filteredCustomers.length === 0 && totalLoaded > 0 ? (
            <EmptyStateBlock
              Icon={GraduationCap}
              title={searchActive ? "No admissions match your search" : "No admissions in this view"}
              description={
                searchActive
                  ? "Try different keywords or clear the search box. The header search is combined with this field."
                  : "Try another status filter or date range under Filters."
              }
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          ) : currentCustomers.length > 0 ? (
            currentCustomers.map((customer, index) => (
              <div
                className={`ecom-customer-card${bulkSelectMode && canBulkDelete() ? " ecom-customer-card--selectable" : ""}`}
                key={customer.id || index}
                onClick={() => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  navigate(`/admission-detail/${customer.id}`);
                }}
                style={{ cursor: bulkSelectMode && canBulkDelete() ? "default" : "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (bulkSelectMode && canBulkDelete()) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/admission-detail/${customer.id}`);
                  }
                }}
              >
                {bulkSelectMode && canBulkDelete() && (
                  <label
                    className="admission-card-checkbox-wrap"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="admission-card-checkbox-input"
                      checked={selectedIds.includes(Number(customer.id))}
                      onChange={() => toggleSelectId(customer.id)}
                      aria-label={`Select ${customer.name || "admission"}`}
                    />
                    <span className="admission-card-checkbox-face" aria-hidden />
                  </label>
                )}
                <div className="admission-card__hero">
                  <div className="admission-card__hero-top">
                    <AdmissionCardAvatar apiBase={url} studentImage={customer.studentImage} />
                    <div className="admission-card__intro">
                      {customer.name && (
                        <h2 className="ecom-customer-name admission-card__name">{customer.name}</h2>
                      )}
                      {customer.admission_id && (
                        <div className="admission-card__sub" title="Admission reference">
                          {customer.admission_id}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admission-card__control-row">
                    {customer.createdAt ? (
                      <div
                        className="ecom-customer-time admission-card__time"
                        title="Submitted (India time)"
                      >
                        <Calendar size={13} aria-hidden strokeWidth={2.25} />
                        <span>{formatAdmissionDateTimeIST(customer.createdAt)}</span>
                      </div>
                    ) : (
                      <span className="admission-card__control-spacer" aria-hidden />
                    )}
                    <div
                      className="ecom-customer-actions admission-card__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        className="ecom-status-select"
                        aria-label="Enrollment status"
                        value={
                          ["Active", "Deactive", "Paid", "Failed"].includes(customer.status)
                            ? customer.status
                            : "Deactive"
                        }
                        onChange={(e) => handleStatusChange(e, customer)}
                      >
                        <option value="Active">Active</option>
                        <option value="Deactive">Deactive</option>
                        <option value="Paid">Paid</option>
                        <option value="Failed">Failed</option>
                      </select>

                      <button
                        className="ecom-edit-btn"
                        type="button"
                        title="Edit admission"
                        aria-label="Edit admission"
                        onClick={() => {
                          handleEdit(customer);
                        }}
                      >
                        <i className="fa-solid fa-pen"></i>
                      </button>

                      <button
                        className="ecom-delete-btn"
                        type="button"
                        title="Delete admission"
                        aria-label="Delete admission"
                        onClick={() => handleDelete(customer.id)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="admission-card__body">
                  {customer.mobile && (
                    <div className="ecom-customer-row admission-card__row admission-card__row--contact">
                      <Phone className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{customer.mobile}</span>
                    </div>
                  )}

                  {customer.email && (
                    <div className="ecom-customer-row admission-card__row admission-card__row--contact">
                      <Mail className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{customer.email}</span>
                    </div>
                  )}
                  {renderStatusRow(customer)}

                  {customer.payment_id && (
                    <div className="ecom-customer-row admission-card__row admission-card__row--payment">
                      <Receipt className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{customer.payment_id}</span>
                    </div>
                  )}

                  {customer.center && (
                    <div className="ecom-customer-row admission-card__row admission-card__row--place">
                      <MapPin className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>{customer.center}</span>
                    </div>
                  )}

                  {customer.batch?.name && (
                    <div
                      className="ecom-customer-row admission-card__row admission-card__row--batch"
                      title="Linked via batch_id — schedule lives on the batch"
                    >
                      <BadgeCheck className="ecom-customer-icon admission-card__icon" strokeWidth={2} />
                      <span>
                        {customer.batch.name}
                        {customer.batch.start_time && customer.batch.end_time
                          ? ` · ${customer.batch.start_time}–${customer.batch.end_time}`
                          : ""}
                      </span>
                    </div>
                  )}
                </div>

              </div>
            ))
          ) : (
            <EmptyStateBlock
              Icon={GraduationCap}
              title="No admissions yet"
              description={
                totalLoaded === 0
                  ? "Add a new admission to get started."
                  : "Try another status filter or add a new admission."
              }
              className="admission-empty--wide admission-empty-center max-w-full"
            />
          )
        }
        </div>

        {bulkSelectMode && canBulkDelete() && selectedIds.length > 0 && (
          <div className="admission-bulk-dock" role="toolbar" aria-label="Bulk delete">
            <div className="admission-bulk-dock__panel">
              <div className="admission-bulk-dock__text">
                <span className="admission-bulk-dock__n">{selectedIds.length}</span>
                <span>admission{selectedIds.length === 1 ? "" : "s"} marked</span>
              </div>
              <div className="admission-bulk-dock__actions">
                <button type="button" className="admission-bulk-dock__secondary" onClick={clearSelection} disabled={bulkDeleting}>
                  Clear
                </button>
                <button
                  type="button"
                  className="admission-bulk-dock__danger"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  <Trash2 size={17} strokeWidth={2.25} aria-hidden />
                  {bulkDeleting ? "Deleting…" : "Delete selected"}
                </button>
              </div>
            </div>
          </div>
        )}

        {totalPages > 1 && (

          <div className="pagination admission-pagination">
            {/* First Page */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronsLeft size={18} />
            </button>

            {/* Previous Page */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Current Page Number Only */}
            <button className="pagination-btn active">
              {currentPage}
            </button>

            {/* Next Page */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronRight size={18} />
            </button>

            {/* Last Page */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}

      </div>
      <FilterDrawer />
    </>
  );
};

export default Admission;
